import { Link } from 'react-router-dom';

import { useTaskStore } from '@/stores/task-store';
import { CollapsibleWidget } from './collapsible-widget';
import { TaskRow } from '@/components/tasks/TaskRow';
import { formatRelativeDueDate } from '@/utils/task-display-utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OffSeasonPrepWidgetProps {
  onQuickComplete: (id: string) => Promise<void>;
  taskErrors?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Shows tasks due in the current calendar month — the off-season prep scope.
 * RULE-012: This widget coexists with PrepCenter on the dashboard; they
 * intentionally show different data scopes (month-filtered vs next 3 upcoming).
 *
 * Hidden entirely when there are no tasks due this month (returns null).
 * Subscribes to tasksDueThisMonth store slice via selector (RULE-015).
 */
export function OffSeasonPrepWidget({ onQuickComplete, taskErrors = {} }: OffSeasonPrepWidgetProps) {
  // RULE-015: subscribe to live store slice so optimistic removals cause immediate re-renders
  const tasks = useTaskStore((s) => s.tasksDueThisMonth);

  if (tasks.length === 0) return null;

  const visibleTasks = tasks.slice(0, 3);
  const hasMore = tasks.length > 3;
  const today = new Date().toISOString().slice(0, 10);

  // Count badge in header
  const titleExtra = (
    <span
      className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-[var(--color-accent)] text-[var(--color-text-display)] text-body-xs font-medium"
      data-testid="off-season-prep-count"
    >
      {tasks.length}
    </span>
  );

  // Collapsed one-line summary — inline in header row
  const nextTask = tasks[0];
  const nextRel = formatRelativeDueDate(nextTask.dueDate, today);
  const collapsedSummary = `${tasks.length} · Next: ${nextTask.title} (${nextRel})`;

  return (
    <CollapsibleWidget
      id="off-season-prep"
      title="PREP THIS MONTH"
      titleExtra={titleExtra}
      summary={collapsedSummary}
      data-testid="collapsible-widget-off-season-prep"
    >
      <div
        className="bg-[var(--color-surface)] border-x border-b border-[var(--color-border)] rounded-b-lg rounded-t-none overflow-hidden"
        data-testid="off-season-prep-widget"
      >
        {visibleTasks.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            seasonId={task.seasonId}
            onToggleComplete={() => {
              void onQuickComplete(task.id);
            }}
            onQuickComplete={onQuickComplete}
            errorMessage={taskErrors[task.id]}
          />
        ))}

        {hasMore && (
          <div className="px-4 py-2 border-t border-[var(--color-border)]/60">
            <Link
              to="/prep"
              className="text-body-sm text-[var(--color-accent)] hover:text-[var(--color-accent-hover)]"
              data-testid="off-season-prep-view-all"
            >
              View all {tasks.length} tasks →
            </Link>
          </div>
        )}
      </div>
    </CollapsibleWidget>
  );
}
